import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email, password }
          : { email, password, family_name: familyName || "Our Family" };
      const { data } = await api.post(path, payload);
      await login(data.token, data.family);
      toast.success(mode === "login" ? "Welcome back!" : "Family created! 🎉");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg,#FFF7E6 0%,#FDE8F0 45%,#EEE7FE 100%)" }}>
      {/* floating deco */}
      {["🧹", "🫧", "🍃", "⭐", "🧦", "💩", "🗑️", "✨"].map((e, i) => (
        <span key={i} className="particle" style={{
          left: `${(i * 12 + 4) % 96}%`, fontSize: 34,
          animationDuration: `${8 + (i % 5)}s`, animationDelay: `${i * 0.9}s`,
        }}>{e}</span>
      ))}

      <div className="card-tactile w-full max-w-md p-8 relative z-10">
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">🎡</div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Family Chore Wheel
          </h1>
          <p className="text-slate-500 mt-1 font-medium">
            One family login. Everyone plays. Chores made fun.
          </p>
        </div>

        <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-xl">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all ${
                mode === m ? "bg-white shadow text-slate-900" : "text-slate-500"
              }`}
            >
              {m === "login" ? "Sign In" : "Create Family"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Family Name</label>
              <input
                data-testid="family-name-input"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="The Smiths"
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#FF5A8B] outline-none font-medium"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Shared Family Email</label>
            <input
              data-testid="email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="family@email.com"
              className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#FF5A8B] outline-none font-medium"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Family Password</label>
            <input
              data-testid="password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#FF5A8B] outline-none font-medium"
            />
          </div>
          {error && (
            <p data-testid="auth-error" className="text-sm font-semibold text-red-500 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-tactile w-full bg-[#FF5A8B] text-white py-3 text-lg"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign In 🚪" : "Create Family 🏡"}
          </button>
        </form>
        <p className="text-center text-xs text-slate-400 mt-5">
          Everyone in the family signs in with the same email & password,
          then picks who they are.
        </p>
      </div>
    </div>
  );
}
